import { Observable, Subject, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/internal/operators';
import * as socketIO from "socket.io-client";
import { copyObject } from '../../helpers';
import { IPrediction } from '../../interfaces/stadtbus/predictions'
import { lazyInject, provideSingleton } from '../../inversify';
import Configuration from './../configuration';
import TimetableService from './timetable_service';

/**
 * Function to compare two different sets of [IPrediction]s. Returns true if both are equal and false if not.
 *
 * @param {IPrediction[]} prev
 * @param {IPrediction[]} next
 * @returns {boolean}
 */
export function predictionsEqualsCheck(prev: IPrediction[], next: IPrediction[]): boolean {
  if (prev.length !== next.length) {
    return false;
  }

  for (let i = 0; i < next.length; ++i) {
    const p = prev[i];
    const n = next[i];

    if (p.delay !== n.delay
      || (p.estimatedTime === undefined || n.estimatedTime === undefined) && p.estimatedTime !== n.estimatedTime
      || (p.estimatedTime && n.estimatedTime) && p.estimatedTime.getTime() !== n.estimatedTime.getTime()
      || p.isRealTime !== n.isRealTime
      || p.journeyRef !== n.journeyRef
      || p.lineDestination !== n.lineDestination
      || p.lineName !== n.lineName
      || p.operationDate !== n.operationDate
      || (p.shouldTime === undefined || n.shouldTime === undefined) && p.shouldTime !== n.shouldTime
      || (p.shouldTime && n.shouldTime) && p.shouldTime.getTime() !== n.shouldTime.getTime()
      || p.stopID !== n.stopID
      || p.tripID !== n.tripID
      || p.visitNumber !== n.visitNumber
      || p.visited !== n.visited) {
      return false;
    }
  }

  return true;
}

@provideSingleton(PredictionsService)
class PredictionsService {
  @lazyInject(Configuration)
  private readonly configuration: Configuration;
  @lazyInject(TimetableService)
  private readonly timetableService: TimetableService;

  /**
   * Get all predictions / stops at a [stopId]. If [onlyToday] is true, it only displays stops from the current day.
   * If false, also events from future days might be returned. The function ensures, that a new set of predictions
   * is emitted at least every 30 seconds, even if no changes occurred.
   *
   * @param {string} stopId
   * @param {boolean} onlyToday
   * @returns {Observable<IPrediction[]>}
   */
  public onStopPredictions(stopId: string, onlyToday: boolean = true): Observable<IPrediction[]> {
    return this.timetableService.onStopTimetable(stopId, onlyToday)
      .pipe(
        distinctUntilChanged(predictionsEqualsCheck),
        this.enrichStopPredictions({'stopId': stopId}),
        this.callAtLeastEach(30),
        this.calculateVisited(),
        map(predictions => predictions.filter(p => !p.visited)),
        map(predictions => this.sortPredictions(predictions)),
      );
  }

  /**
   * Get all predictions / stops of a journey with [journeyRef] and [operationDate]. The function ensures, that a new
   * set of predictions is emitted at least every 30 seconds, even if no changes occurred.
   *
   * @param {string} journeyRef
   * @param {string} operationDate
   * @returns {Observable<IPrediction[]>}
   */
  public onTripPredictions(journeyRef: string, operationDate: string): Observable<IPrediction[]> {
    return this.timetableService.onTripTimetable(journeyRef, operationDate)
      .pipe(
        distinctUntilChanged(predictionsEqualsCheck),
        this.enrichStopPredictions({'journeyRef': journeyRef, 'operationDate': operationDate}),
        this.callAtLeastEach(30),
        this.calculateVisited(),
      );
  }

  public sortPredictions(predictions: IPrediction[]): IPrediction[] {
    // Sort by time
    predictions.sort((a, b) => {
      if (a.shouldTime.getTime() < b.shouldTime.getTime()) {
        return -1;
      } else if (a.shouldTime.getTime() > b.shouldTime.getTime()) {
        return 1;
      } else {
        return a.lineName < b.lineName ? -1 : 1;
      }
    });

    return predictions;
  }

  /**
   * Filters the incoming predictions such that only the next departure per line is displayed.
   *
   * @returns {(source: Observable<IPrediction[]>) => Observable<IPrediction[]>}
   */
  public filterNextDeparturePerLine(maxCycleDelay?: number) {
    return (source: Observable<IPrediction[]>): Observable<IPrediction[]> =>
      source.pipe(
        map(predictions => this.filterNextDeparturePerLineInstant(predictions, maxCycleDelay)),
      );
  }

  public filterNextDeparturePerLineInstant(predictions: IPrediction[], maxCycleDelay?: number) {
    // Array to store the new predictions.
    const newPredictions: IPrediction[] = [];
    // Function to generate the distinct key of each prediction.
    const key = (p1: IPrediction) => `${p1.lineName}.${p1.lineDestination}`;
    // const comp = (d1: Date, d2: Date) => ((d1 > d2) ? 1 : (d2 < d1) ? -1 : 0);

    // Only allow one prediction per key.
    // predictions.sort((a,b) => comp(a.shouldTime, b.shouldTime)).forEach(p => {
    // if (newPredictions.find(ps => key(ps) === key(p)) === undefined) {
    predictions.forEach(p => {
    if (newPredictions.find(ps => key(ps) === key(p) && (maxCycleDelay === undefined || ps.delay < maxCycleDelay)) === undefined) {
        newPredictions.push(p);
      }
    });

    // Sort by name and direction.
    return newPredictions
      .sort((a, b) => a.lineDestination.localeCompare(b.lineDestination))
      .sort((a, b) => a.lineName.localeCompare(b.lineName));
  }

  /**
   * Get the raw predictions from the backend.
   *
   * @param {string} parameters
   * @returns {Observable<IPrediction[]>}
   */
  public onPrediction(parameters: { [key: string]: any }) {
    return new Observable<IPrediction[]>(observer => {
      const stopTimerSubject = new Subject<boolean>();

      const socket = socketIO(this.configuration.socketIoEndpoint(), {
        path: this.configuration.socketIoPath(),
        query: {
          ...parameters,
          'type': 'TIMETABLE',
        },
      });

      socket.on('predictions', (data: IPrediction[]) => {
        // stop the timer since we received the predictions.
        stopTimerSubject.next();

        data.forEach(p => {
          p.estimatedTime = p.estimatedTime && new Date(p.estimatedTime);
          p.shouldTime = new Date(p.shouldTime);
        });
        observer.next(data);
      });

      // Emit empty predictions after 500ms in order to display the raw timetable-data if the socket connection is too slow
      timer(500)
        .pipe(takeUntil(stopTimerSubject))
        .subscribe(() => observer.next([]));

      // close the socket and stop the timer on complete
      return () => {
        socket.close();
        stopTimerSubject.next(true);
      }
    });
  }

  public callAtLeastEach(seconds: number) {
    return (source: Observable<IPrediction[]>) =>
      source.pipe(
        switchMap(predictions =>
          timer(0, seconds * 1000)
            .pipe(map(() => predictions)),
        ));
  }

  public calculateVisited(nowTimeFunction: () => Date = () => new Date()) {
    return (source: Observable<IPrediction[]>) =>
      source.pipe(
        map(predictions => {
          const nowTime = nowTimeFunction();
          predictions.forEach((p, idx, arr) => {
            if (!p.isRealTime) {
              const prev = idx > 0 && arr[idx - 1];
              if (prev && prev.journeyRef === p.journeyRef && prev.operationDate === p.operationDate && !prev.visited && prev.isRealTime) {
                // Special case: If the previous prediction (i.e. stop) has not been visited and contains real-time
                // information, the following stop should also not be marked as visited. This can happen if stops exist
                // in the static timetable source but are missing in the dynamic predictions source.
                p.visited = false;

                // We also carry over the delay and the isRealTime flag
                p.delay = prev.delay;
                p.isRealTime = true;
              } else {
                p.visited = p.estimatedTime !== undefined ? p.estimatedTime < nowTime : p.shouldTime < nowTime
              }
            }
          });
          return predictions;
        }),
      );
  }

  /**
   * Takes the incomplete predictions from the TimetableService and enriches them with real-time informations. The
   * predictions are automatically updated by the Observable.
   *
   * @returns {(source: Observable<IPrediction[]>) => Observable<any>}
   */
  private enrichStopPredictions(parameters: { [key: string]: any }) {
    return (source: Observable<IPrediction[]>) =>
      source.pipe(
        switchMap((timetable: IPrediction[]) =>
          this.onPrediction(parameters)
            .pipe(
              map(predictions =>
                copyObject(timetable).map(p => {
                  const prediction = predictions
                    .find(pred =>
                      p.journeyRef === pred.journeyRef
                      && p.operationDate === pred.operationDate
                      && p.stopID === pred.stopID
                    );

                  if (prediction !== undefined) {
                    return {
                      ...p,
                      delay: Math.round(prediction.delay / 60),
                      estimatedTime: prediction.estimatedTime,
                      isRealTime: true,
                      tripID: prediction.tripID,
                      visited: prediction.visited,
                    } as IPrediction;
                  } else {
                    return p;
                  }
                })
              ))),

        // debounce since the amount of updates can be quite high on page-load.
        debounceTime(50));
  }
}

export default PredictionsService;



// WEBPACK FOOTER //
// ./src/api/stadtbus/predictions_service.ts